import type { ManufacturerRatingKind } from '@/lib/manufacturer-ratings'
import { formatMoney, formatPercent } from '@/lib/utils'
import type { components } from '@/api/schema'

type Manufacturer = components['schemas']['ManufacturerListRead']
type FreightRule = Pick<
  components['schemas']['ManufacturerFreightRuleRead'],
  | 'id'
  | 'rule_type'
  | 'ff_threshold_net'
  | 'below_threshold_percent'
  | 'origin_zip_codes'
  | 'priority'
  | 'effective_from'
  | 'effective_to'
  | 'enabled'
  | 'conditions'
>

/** The ratings the list endpoint reports (USA-1273, USA-1272). */
export const LIST_RATING_KINDS: ManufacturerRatingKind[] = ['quality_tier', 'preference', 'pricing']

function isCurrent(rule: FreightRule, today: string) {
  return (
    rule.enabled &&
    (!rule.effective_from || rule.effective_from <= today) &&
    (!rule.effective_to || rule.effective_to >= today)
  )
}

function isLimited(rule: FreightRule) {
  const c = rule.conditions
  return (
    !!rule.origin_zip_codes ||
    (!!c &&
      (c.min_order_net != null ||
        c.ff_min_units != null ||
        c.direct_orders_only ||
        c.discount_ids.length > 0 ||
        c.category_ids.length > 0 ||
        c.exclude_category_ids.length > 0 ||
        c.destination_states.length > 0))
  )
}

function freightRuleText(rule: FreightRule) {
  const pieces = rule.conditions?.ff_min_units
  const piecesText = pieces != null ? `${pieces} ${pieces === 1 ? 'piece' : 'pieces'}` : null
  const threshold =
    rule.ff_threshold_net != null
      ? `${formatMoney(rule.ff_threshold_net)} net${piecesText ? ` or ${piecesText}` : ''}`
      : (piecesText ?? formatMoney(rule.ff_threshold_net))
  const percent = formatPercent(Number(rule.below_threshold_percent ?? 0) * 100)
  switch (rule.rule_type) {
    case 'always_free':
      return 'Always free freight'
    case 'threshold':
      return `Free freight at ${threshold}`
    case 'threshold_with_percent':
      return `Free freight at ${threshold}, else ${percent}`
    case 'percent_only':
      return `Freight ${percent} of order`
    default:
      return null
  }
}

/** The manufacturer's free-freight program in one line; anything more lives on its page. */
export function freightProgram(mfr: Manufacturer, today = new Date().toISOString().slice(0, 10)) {
  return freightRulesProgram(mfr.freight_rules ?? [], today)
}

/** A set of freight rules (the manufacturer's, or one source's) in one line, plus whether more applies. */
export function freightRulesProgram(
  freightRules: FreightRule[],
  today = new Date().toISOString().slice(0, 10),
) {
  const rules = freightRules
    .filter((rule) => isCurrent(rule, today))
    .sort((a, b) => b.priority - a.priority || a.id - b.id)
  const first = rules[0]
  const text = first ? freightRuleText(first) : null
  if (!first || !text) return null
  return { text, more: rules.length > 1 || isLimited(first) }
}
